import { Badge } from '@/components/ui/badge';
import type { DataProvenance } from '../types';

const PROVENANCE_STYLES: Record<DataProvenance, string> = {
  live: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  derived: 'bg-sky-50 text-sky-700 border-sky-200',
  demo: 'bg-violet-50 text-violet-700 border-violet-200',
  missing: 'bg-gray-50 text-gray-500 border-gray-200',
};

const PROVENANCE_LABELS: Record<DataProvenance, string> = {
  live: 'Live',
  derived: 'Derived',
  demo: 'Demo',
  missing: 'Missing',
};

interface ProvenanceBadgeProps {
  provenance: DataProvenance;
  className?: string;
}

/**
 * Field/section-level provenance label. Live and demo data are never silently
 * blended — every section of a store profile states where its data comes from.
 */
export function ProvenanceBadge({ provenance, className }: ProvenanceBadgeProps) {
  return (
    <Badge
      variant="outline"
      className={`text-[10px] font-medium ${PROVENANCE_STYLES[provenance]} ${className ?? ''}`}
    >
      {PROVENANCE_LABELS[provenance]}
    </Badge>
  );
}

interface ProvenanceSectionHeaderProps {
  title: React.ReactNode;
  provenance: DataProvenance;
  note?: string;
  actions?: React.ReactNode;
}

/** Section heading with an attached provenance label and optional note/action row. */
export function ProvenanceSectionHeader({ title, provenance, note, actions }: ProvenanceSectionHeaderProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <span className="truncate">{title}</span>
        <ProvenanceBadge provenance={provenance} />
      </div>
      {note && <p className="text-xs text-gray-500 w-full sm:w-auto sm:flex-1 sm:text-right">{note}</p>}
      {actions}
    </div>
  );
}
